import { ref } from "vue";
import { defineStore } from "pinia";
import { UserModel } from "@/models/User/UserModel";
import { UserInterface } from "@/interfaces/UserInterface";
import { apiLogInUser, apiLogOutUser } from "@/services/global/global.api";
import { LoginModel } from "@/models/LoginModel";
import router from "@/router";
import { notifyService } from "@/helpers/NotifyService";
import { useToast } from "primevue/usetoast";

export const useGlobalStore = defineStore("global-store", () => {
  const user = ref<UserInterface>(new UserModel());
  const userInput = ref<LoginModel>(new LoginModel());
  const isAuthenticated = ref<Boolean>(false);
  const sidebarVisible = ref(true);
  const isDarkMode = ref(false);
  const selectedClientId = ref<string | null>(null);

  const setClientId = (id: number) => {
    const idToString = id.toString();
    selectedClientId.value = idToString;
  };

  const clearClientId = () => {
    selectedClientId.value = null;
  };

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    sidebarVisible.value = !sidebarVisible.value;
  };

  // Toggle theme between dark and light mode
  const toggleTheme = () => {
    isDarkMode.value = !isDarkMode.value;
    document.documentElement.classList.toggle("dark-mode", isDarkMode.value);
    localStorage.setItem("theme", isDarkMode.value ? "dark" : "light");
  };

  // Check saved theme from localStorage and apply it
  const checkTheme = () => {
    const savedTheme = localStorage.getItem("theme") || "light";
    isDarkMode.value = savedTheme === "dark";
    if (isDarkMode.value) {
      document.documentElement.classList.add("dark-mode");
    } else {
      document.documentElement.classList.remove("dark-mode");
    }
  };

  // Check token validity and restore user session
  function checkTokenValidity() {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const tokenData = parseJwt(token);

      if (tokenData) {
        // Check if the token has `exp` and is still valid
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        if (tokenData.exp && tokenData.exp > currentTime) {
          // Token is valid
          user.value = new UserModel(tokenData);
          isAuthenticated.value = true;
        } else {
          // Token expired or malformed, remove it
          localStorage.removeItem("accessToken");
          isAuthenticated.value = false;
          user.value = new UserModel();
        }
      } else {
        // If token parsing fails (malformed token), log out the user
        localStorage.removeItem("accessToken");
        isAuthenticated.value = false;
        user.value = new UserModel();
      }
    }
  }

  // User login
  function logInUser() {
    // Call the API for login
    apiLogInUser(userInput.value)
      .then((response: any) => {
        if (response.data) {
          // Save token to localStorage
          localStorage.setItem("accessToken", response.data.access_token);

          const tokenData = parseJwt(response.data.access_token);

          user.value = new UserModel(tokenData);
          isAuthenticated.value = true;

          router.push("/");
        }
      })
      .catch((error: any) => {
        notifyService.notifyUserAboutApiError(error);
      });
  }

  // User logout
  function logOutUser() {
    apiLogOutUser()
      .then((response: any) => {
        // Clear the token and user data
        localStorage.removeItem("accessToken");

        user.value = new UserModel(); // Clear user data
        userInput.value = new LoginModel(); // Clear user input
        isAuthenticated.value = false;

        // Reset theme to light mode after logout
        isDarkMode.value = false;
        // document.documentElement.classList.remove("dark-mode");
        // localStorage.setItem("theme", "light");

        // Redirect to login page
        router.push("/login").then(() => window.location.reload());
      })
      .catch((error: any) => {
        console.error("Unexpected error during logout:", error.message);
      });
  }

  // Parse JWT token
  function parseJwt(token: string) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = decodeURIComponent(
        atob(base64Url)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(base64);
    } catch (e) {
      console.error("Error parsing token:", e);
      return null;
    }
  }

  window.addEventListener("storage", () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      isAuthenticated.value = false;
      user.value = new UserModel();
      router.push("/login");
    }
  });

  // Initial checks when the app loads
  checkTokenValidity();
  checkTheme();

  return {
    user,
    userInput,
    logInUser,
    isAuthenticated,
    checkTokenValidity,
    sidebarVisible,
    toggleSidebar,
    logOutUser,
    isDarkMode,
    toggleTheme,
    selectedClientId,
    setClientId,
    clearClientId,
  };
});
